import { format, isValid, parse } from 'date-fns';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DateQuestionProps } from '../../types';
import { DatePicker } from '../components/date-picker';
import { HACK_defaultBirthday } from '@/app/components/questionnaireV2/hooks/hack-questionnaire';
import { DATE_CONSTANTS } from './date-constants';

export const DateQuestion = ({ question }: DateQuestionProps) => {
  const { control, setError, clearErrors, watch } = useFormContext();
  const currentValue = watch(question.slug);

  // Keep track of the input value separately from the form value
  const [localInputValue, setLocalInputValue] = React.useState(() => {
    if (!currentValue) return '';
    const selectedDate = parse(currentValue, DATE_CONSTANTS.STORAGE_FORMAT, new Date());
    return isValid(selectedDate) ? format(selectedDate, DATE_CONSTANTS.DISPLAY_FORMAT) : '';
  });

  function parseInputDate(dateString: string, fieldName: string): Date | null {
    try {
      // Check if the string is effectively empty (only slashes or spaces)
      if (!dateString.trim() || dateString.replace(/[/\s]/g, '') === '') {
        setError(fieldName, {
          type: 'manual',
          message: 'Please input a valid date',
        });
        return null;
      }

      // Extract parts from the date string
      // handles for MM/DD/YY and MM/DD/YYYY formats
      const parts = dateString.split('/');
      if (parts.length !== 3) return null;

      const [month, day, yearStr] = parts;
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      const yearNum = parseInt(yearStr, 10);

      if (isNaN(monthNum) || isNaN(dayNum) || isNaN(yearNum)) return null;

      // Validate month is between 1-12
      if (monthNum < 1 || monthNum > 12) {
        setError(fieldName, {
          type: 'manual',
          message: 'Month must be between 1 and 12',
        });
        return null;
      }

      let fullYear = yearNum;
      // Handle 2-digit years
      if (yearStr.length === 2) {
        const currentYear = new Date().getFullYear();
        const currentYearLastTwoDigits = currentYear % 100;
        fullYear = yearNum <= currentYearLastTwoDigits ? 2000 + yearNum : 1900 + yearNum;
      }

      const daysInMonth = new Date(fullYear, monthNum, 0).getDate();
      if (dayNum > daysInMonth) {
        const monthName = format(new Date(fullYear, monthNum - 1, 1), 'MMMM');
        setError(fieldName, {
          type: 'manual',
          message: `${monthName} ${fullYear} only has ${daysInMonth} days`,
        });
        return null;
      }

      // Create a normalized date string for date-fns parsing
      const normalizedDateString = `${monthNum.toString().padStart(2, '0')}/${dayNum.toString().padStart(2, '0')}/${fullYear}`;

      // Parse with MM/dd/yyyy format
      const date = parse(normalizedDateString, 'MM/dd/yyyy', new Date());
      if (!isValid(date)) {
        setError(fieldName, {
          type: 'manual',
          message: 'Invalid date combination',
        });
        return null;
      }
      // Clear any previous errors if validation passes
      clearErrors(fieldName);
      return date;
    } catch {
      setError(fieldName, {
        type: 'manual',
        message: 'Please enter a valid date',
      });
      return null;
    }
  }

  const [userCleared, setUserCleared] = React.useState(false);

  // Update local input when form value changes (e.g., from calendar selection)
  React.useEffect(() => {
    // Only update if there's a value and user hasn't manually cleared
    if (currentValue && !userCleared) {
      const selectedDate = parse(currentValue, DATE_CONSTANTS.STORAGE_FORMAT, new Date());
      if (isValid(selectedDate)) {
        setLocalInputValue(format(selectedDate, DATE_CONSTANTS.DISPLAY_FORMAT));
      }
    } else if (!currentValue && !userCleared) {
      // Only clear if it wasn't user-initiated
      setLocalInputValue('');
    }
  }, [currentValue, userCleared]);

  return (
    <Controller
      control={control}
      name={question.slug}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const selectedDate = value
          ? parse(value, DATE_CONSTANTS.STORAGE_FORMAT, new Date())
          : question.slug === 'question_birthday'
            ? HACK_defaultBirthday()
            : null;
        const formattedPlaceholder =
          question.slug === 'question_birthday'
            ? format(HACK_defaultBirthday(), DATE_CONSTANTS.DISPLAY_FORMAT)
            : DATE_CONSTANTS.DISPLAY_FORMAT;

        const handleDateSelect = (date: Date) => {
          const dateString = format(date, DATE_CONSTANTS.STORAGE_FORMAT);
          setUserCleared(false); // Reset flag when selecting from calendar
          onChange(dateString);
          setLocalInputValue(format(date, DATE_CONSTANTS.DISPLAY_FORMAT));
        };

        const formatDateInput = (input: string, previousValue: string): string => {
          // If input is empty or only contains slashes/spaces, return empty
          if (!input.trim() || input.replace(/[/\s]/g, '') === '') {
            return '';
          }

          // Remove all non-numeric characters
          const numbers = input.replace(/\D/g, '');
          const prevNumbers = previousValue.replace(/\D/g, '');

          // If no numbers, return empty
          if (!numbers) {
            return '';
          }

          // Check if user is typing in the year field (after MM/DD/)
          const isTypingYear = numbers.length > 4 && prevNumbers.length >= 4;

          // Apply formatting based on length
          if (numbers.length === 1) {
            // For month: if first digit is greater than threshold, prefix with 0
            const firstDigit = parseInt(numbers[0], 10);
            if (firstDigit > DATE_CONSTANTS.MONTH_AUTO_PREFIX_THRESHOLD) {
              return `0${numbers}/`;
            }
            return numbers;
          } else if (numbers.length === 2) {
            return numbers;
          } else if (numbers.length === 3) {
            const month = numbers.slice(0, 2);
            const dayFirstDigit = parseInt(numbers[2], 10);
            // For day: if first digit is greater than threshold, prefix with 0
            if (dayFirstDigit > DATE_CONSTANTS.DAY_AUTO_PREFIX_THRESHOLD) {
              return `${month}/0${dayFirstDigit}`;
            }
            return `${month}/${numbers.slice(2)}`;
          } else if (numbers.length <= 4) {
            return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
          } else if (isTypingYear && numbers.length === 7) {
            // Special case: user typed a digit after 07 was auto-formatted
            // Extract the last 2 digits as the intended year
            const month = numbers.slice(0, 2);
            const day = numbers.slice(2, 4);
            const year = numbers.slice(5, 7); // Skip the auto-formatted 0
            return `${month}/${day}/${year}`;
          } else {
            // Normal formatting - limit to 6 digits for MM/DD/YY format
            return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 6)}`;
          }
          return input;
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = e.target.value;
          const cursorPosition = e.target.selectionStart || 0;
          // Format the input value
          const formattedValue = formatDateInput(rawValue, localInputValue);

          // Always update the local input value to allow typing
          setLocalInputValue(formattedValue);

          // Calculate new cursor position after formatting
          setTimeout(() => {
            if (e.target) {
              let newCursorPos = cursorPosition;

              // Count characters added by formatting
              const formattedLength = formattedValue.length;
              const rawLength = rawValue.length;

              // If we added characters (0 prefix or slashes), adjust cursor
              if (formattedLength > rawLength) {
                newCursorPos = cursorPosition + (formattedLength - rawLength);
              }

              e.target.setSelectionRange(newCursorPos, newCursorPos);
            }
          }, 0);

          if (!formattedValue || !formattedValue.trim()) {
            setUserCleared(true);
            onChange(undefined);
            return;
          }

          // Only attempt parsing if we have the complete format
          if (formattedValue.length === DATE_CONSTANTS.DISPLAY_FORMAT.length) {
            const parsedDate = parseInputDate(formattedValue, question.slug);
            if (parsedDate) {
              const dateString = format(parsedDate, DATE_CONSTANTS.STORAGE_FORMAT);
              setUserCleared(false); // Reset flag when valid date is entered
              onChange(dateString);
            }
          }
        };

        return (
          <div className="questionnaire-question-content">
            <DatePicker
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              error={error?.message || ''}
              inputValue={localInputValue}
              onInputChange={handleInputChange}
              placeholder={formattedPlaceholder}
            />
          </div>
        );
      }}
    />
  );
};
